import { useMemo } from "react";
import { getWorkflowDefinition, getWorkflowStep } from "../registry";
import {
  brandWorkflowOverlay,
  brandWorkflowOverlaySignature,
} from "../overlays/brands";
import {
  mergeWorkflowOverlays,
  surfaceWorkflowOverlay,
  surfaceWorkflowOverlaySignature,
} from "../overlays/surfaces";
import type {
  ResolvedWorkflowStep,
  WorkflowDefinition,
  WorkflowContext,
  WorkflowOverlayContext,
} from "../types";

export type ResolvedWorkflowDefinition = WorkflowDefinition & {
  resolvedSteps: ResolvedWorkflowStep[];
};

function toWorkflowOverlayContext(context: WorkflowContext): WorkflowOverlayContext {
  return {
    brief: {
      surface: context.brief.surface,
      platform: context.brief.platform,
    },
    brand: context.brand
      ? {
          id: context.brand.id,
          targetAudience: context.brand.target_audience ?? null,
          strategyAudienceWho: context.brand.strategy?.audience?.who ?? null,
          strategyArtifactStatus: context.brand.canonical_artifacts?.strategy?.status ?? null,
          voiceArtifactStatus: context.brand.canonical_artifacts?.voice?.status ?? null,
        }
      : null,
  };
}

function normalizeWorkflowOverlayContext(
  context: WorkflowContext | WorkflowOverlayContext,
): WorkflowOverlayContext {
  return "campaignId" in context ? toWorkflowOverlayContext(context) : context;
}

export function resolveWorkflowDefinition(
  workflowId: string,
  context: WorkflowContext | WorkflowOverlayContext,
): ResolvedWorkflowDefinition {
  const overlayContext = normalizeWorkflowOverlayContext(context);
  const definition = getWorkflowDefinition(workflowId);
  if (!definition) {
    throw new Error(`Unknown workflow definition: ${workflowId}`);
  }

  const overlays = [
    surfaceWorkflowOverlay(overlayContext),
    brandWorkflowOverlay(overlayContext),
  ].filter((overlay): overlay is NonNullable<typeof overlay> => Boolean(overlay));
  const merged = mergeWorkflowOverlays(definition, getWorkflowStep, overlays);

  return {
    ...definition,
    steps: merged.steps,
    resolvedSteps: merged.resolvedSteps,
  };
}

export function useWorkflowDefinition(
  workflowId: string,
  context: WorkflowContext,
): ResolvedWorkflowDefinition {
  const overlayContext = toWorkflowOverlayContext(context);
  const surfaceSignature = surfaceWorkflowOverlaySignature(overlayContext);
  const brandSignature = brandWorkflowOverlaySignature(overlayContext);
  return useMemo(
    () => resolveWorkflowDefinition(workflowId, overlayContext),
    [
      // These signatures must include every context field that can change the
      // resolved surface/brand overlays. Keep them in lockstep with the
      // overlay signature helpers rather than depending on the full context.
      workflowId,
      surfaceSignature,
      brandSignature,
    ],
  );
}
